import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { Link } from 'react-router-dom';
import { Archive, ArrowLeft } from 'lucide-react';
import PortalBackground from '../../components/PortalBackground';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const deptStyles = {
  Sales: 'bg-red-100 text-red-800',
  Operations: 'bg-blue-100 text-blue-800',
  Finance: 'bg-green-100 text-green-800',
  Technology: 'bg-purple-100 text-purple-800',
  Marketing: 'bg-orange-100 text-orange-800',
  HR: 'bg-yellow-100 text-yellow-800',
  General: 'bg-gray-100 text-gray-800',
};

const formatShortDate = (date) =>
  date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const dateToISO = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const avatarStyles = [
  'bg-gradient-to-br from-red-500 to-pink-500',
  'bg-gradient-to-br from-purple-500 to-indigo-500',
  'bg-gradient-to-br from-orange-500 to-amber-500',
  'bg-gradient-to-br from-teal-500 to-cyan-500',
];

const sessionFromDoc = (docSnap) => {
  const data = docSnap.data();
  const dateObj = data.date?.toDate ? data.date.toDate() : new Date(data.date);
  return {
    id: docSnap.id,
    date: dateObj,
    isoDate: dateToISO(dateObj),
    eventName: data.eventName || 'Breakthrough Session',
    presenter: data.presenter || '',
    presenterId: data.presenterId || '',
    presenterPhoto: data.presenterPhoto || '',
    presenterInitials: data.presenterInitials || '',
    department: data.department || 'General',
    description: data.description || '',
    startTime: data.startTime || '',
    endTime: data.endTime || '',
    needPresenter: data.needPresenter || false,
    attachments: Array.isArray(data.attachments) ? data.attachments : [],
    archivedAt: data.archivedAt?.toDate ? data.archivedAt.toDate() : data.archivedAt,
  };
};

const initialsFor = (presenter, fallback) => {
  if (!presenter) return fallback;
  return presenter
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const BreakthroughSessionsArchive = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [collapsedMonths, setCollapsedMonths] = useState(new Set());
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    const fetchArchived = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const q = query(
          collection(db, 'breakthroughSessions'),
          where('archived', '==', true)
        );
        const snap = await getDocs(q);
        const list = snap.docs.map((docSnap) => sessionFromDoc(docSnap));
        setSessions(list);
      } catch (err) {
        console.error('Failed to load archive', err);
        setLoadError('Failed to load archive. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchArchived();
  }, []);

  const byMonth = useMemo(() => {
    const map = {};
    sessions.forEach((session) => {
      const key = session.isoDate ? session.isoDate.substring(0, 7) : `${new Date(session.date).getFullYear()}-${String(new Date(session.date).getMonth() + 1).padStart(2, '0')}`;
      if (!map[key]) map[key] = [];
      map[key].push(session);
    });
    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => new Date(a.date) - new Date(b.date));
    });
    return Object.entries(map)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, slots]) => {
        const [y, m] = key.split('-').map(Number);
        return { key, label: `${MONTH_NAMES[m - 1]} ${y}`, slots };
      });
  }, [sessions]);

  const toggleMonth = (key) => {
    setCollapsedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/crm/fulfillment/breakthroughsfufillment"
            aria-label="Back to Breakthrough Sessions"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white text-center flex-1">
            Breakthrough Sessions Archive
          </h1>
          <div className="w-[100px]" />
        </div>

        {loading && (
          <div className="text-center text-gray-600 dark:text-gray-300 mb-4">Loading archive…</div>
        )}
        {loadError && (
          <div className="text-center text-sm text-red-600 dark:text-red-400 mb-4">{loadError}</div>
        )}

        {!loading && !loadError && byMonth.length === 0 && (
          <div className="text-center text-gray-600 dark:text-gray-400 py-12">
            No archived sessions yet.
          </div>
        )}

        <div className="space-y-6">
          {byMonth.map(({ key, label, slots }) => {
            const isCollapsed = collapsedMonths.has(key);
            return (
              <div key={key} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleMonth(key)}
                  onKeyDown={(e) => e.key === 'Enter' && toggleMonth(key)}
                  className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <Archive className="h-4 w-4 text-gray-500" />
                  <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {label}
                  </span>
                  <span className="text-sm text-gray-500">
                    ({slots.length} session{slots.length !== 1 ? 's' : ''})
                  </span>
                  <span className="text-sm text-gray-500 ml-auto">{isCollapsed ? '▶' : '▼'}</span>
                </div>
                {!isCollapsed && (
                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {slots.map((session) => (
                      <div
                        key={session.id}
                        onClick={() => setSelectedSession(session)}
                        className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all"
                      >
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          {formatShortDate(session.date)}
                        </div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                          {session.eventName}
                        </div>
                        <span
                          className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            deptStyles[session.department] || 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {session.department || 'Unassigned'}
                        </span>
                        <div className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                          {session.presenter || 'No presenter'}
                        </div>
                        {session.startTime && (
                          <div className="text-xs text-gray-500 mt-1">
                            {session.startTime}
                            {session.endTime ? ` – ${session.endTime}` : ''}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selectedSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setSelectedSession(null)}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="text-center w-full">
                <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {selectedSession.eventName}
                </div>
              </div>
              <button
                onClick={() => setSelectedSession(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 absolute right-4 top-4"
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            <div className="flex items-center gap-3 mb-4 justify-center">
              {selectedSession.presenterPhoto ? (
                <img
                  src={selectedSession.presenterPhoto}
                  alt={selectedSession.presenter || 'Presenter'}
                  className="h-12 w-12 rounded-full object-cover"
                />
              ) : (
                <div
                  className={`h-12 w-12 rounded-full flex items-center justify-center text-white font-semibold ${
                    avatarStyles[
                      (selectedSession.presenter || '').charCodeAt?.(0)
                        ? Math.abs(selectedSession.presenter.charCodeAt(0)) % avatarStyles.length
                        : 0
                    ]
                  }`}
                >
                  {selectedSession.presenterInitials ||
                    initialsFor(selectedSession.presenter, '—')}
                </div>
              )}
              <div className="text-left">
                <div className="text-sm text-gray-500 dark:text-gray-300">Presenter</div>
                <div className="text-base text-gray-900 dark:text-gray-100">
                  {selectedSession.presenter || 'Not set'}
                </div>
              </div>
            </div>

            <div className="space-y-2 text-sm text-gray-800 dark:text-gray-100">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Date</span>
                <span>{formatShortDate(selectedSession.date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Time</span>
                <span>
                  {selectedSession.startTime || '—'}
                  {selectedSession.startTime && selectedSession.endTime ? ' - ' : ''}
                  {selectedSession.endTime}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300">Department</span>
                <span
                  className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                    deptStyles[selectedSession.department] || 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {selectedSession.department || 'Unassigned'}
                </span>
              </div>
              <div>
                <div className="text-gray-600 dark:text-gray-300 mb-1">Description</div>
                <div className="text-gray-900 dark:text-gray-100">
                  {selectedSession.description || 'No description yet.'}
                </div>
              </div>
              <div>
                <div className="text-gray-600 dark:text-gray-300 mb-2">Attachments</div>
                {selectedSession.attachments && selectedSession.attachments.length > 0 ? (
                  <div className="space-y-2">
                    {selectedSession.attachments.map((att, idx) => (
                      <div
                        key={`${selectedSession.id}-att-${idx}`}
                        className="flex items-center justify-between gap-2 py-1.5 px-2 rounded bg-gray-100 dark:bg-gray-800"
                      >
                        <a
                          href={att.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-red-600 dark:text-red-400 hover:underline truncate flex-1"
                        >
                          {att.fileName || 'Download'}
                        </a>
                        <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                          {att.uploadDate ? new Date(att.uploadDate).toLocaleDateString() : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-600 dark:text-gray-300">No attachments.</div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedSession(null)}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BreakthroughSessionsArchive;
